import { useAuthContext } from "auth/AuthProvider";
import { Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

const MainPage = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const workspace = user.current_workspace;
  const initialGroupData = useMemo(() => {
    return {
      name: workspace.name,
    };
  }, [workspace]);
  const [formData, setFormData] = useState<{
    name: string;
  }>(initialGroupData);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Check if form has changes
  useEffect(() => {
    const dataChanged = formData.name !== initialGroupData.name;
    setHasChanges(dataChanged);
  }, [formData, initialGroupData]);

  // Validate form
  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.name.trim()) {
      newErrors.name = t("workspaces.mainPage.nameRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field: "name", value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Mock API call
  const mockApiCall = () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true });
      }, 2000);
    });
  };

  // Handle form submission
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await mockApiCall();
      console.log("Group saved:", formData);
      // Reset hasChanges after successful save
      setHasChanges(false);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if save button should be disabled
  const isSaveDisabled = !hasChanges || !formData.name.trim() || isLoading;

  return (
    <>
      <div className="space-y-6">
        {/* Name Field */}
        <div>
          <label
            htmlFor="name"
            className="mb-3 block text-sm font-medium text-gray-700"
          >
            {t("workspaces.mainPage.nameLabel")}
          </label>
          <div className="relative">
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className={`block w-full rounded-md border px-4 py-3 transition-colors duration-200 ${
                errors.name
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              } focus:outline-none focus:ring-1`}
              placeholder={t("workspaces.mainPage.namePlaceholder")}
              disabled={isLoading}
            />
            {errors.name && (
              <div className="mt-2 flex items-center text-sm text-red-600">
                <Info />
                {errors.name}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaveDisabled}
          className={`inline-flex min-w-[140px] items-center justify-center rounded-md px-6 py-3 text-sm font-medium transition-all duration-200 ${
            isSaveDisabled
              ? "cursor-not-allowed bg-gray-300 text-gray-500"
              : "bg-purple-600 text-white shadow-sm hover:bg-purple-700 hover:shadow-md active:bg-purple-800"
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin" />
              {t("workspaces.mainPage.saving")}
            </>
          ) : (
            t("workspaces.mainPage.saveChanges")
          )}
        </button>
      </div>
    </>
  );
};

export default MainPage;
